import { DelegationStatusEnum } from 'src/app/models/enums/delegation-status-enum';
import { DelegationSettlementDto } from './../../../models/dtos/delegation-settlement-dto';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, Observable, Subject, combineLatest, firstValueFrom, lastValueFrom, throwError } from 'rxjs';
import { catchError, first, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { UpdateDelegationDatesRequest } from 'src/app/contracts/requests/update-delegation-dates-request';
import { DelegationService } from 'src/app/data/delegation.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DelegationInvoiceListDataSource } from '../../delegation-invoice-list/delegation-invoice-list.datasource';
import { DelegationAddInvoiceFormComponent } from './delegation-add-invoice-form/delegation-add-invoice-form.component';
import moment from 'moment';
import { Router } from '@angular/router';
import { Moment } from 'moment';
import { MatStepper } from '@angular/material/stepper';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { ExtendedAlertDialogComponent } from 'src/app/shared/messages/extended-alert-dialog/extended-alert-dialog.component';
import { WarningDialogComponent } from 'src/app/shared/messages/warning-dialog/warning-dialog.component';

@Component({
  selector: 'app-delegation-cost-step',
  templateUrl: './delegation-cost-step.component.html',
  styleUrls: ['./delegation-cost-step.component.scss'],
  standalone: false,
})
export class DelegationCostStepComponent implements OnInit, OnDestroy {
  delegationsInvoicesDataSource = inject(DelegationInvoiceListDataSource);
  private dialog = inject(MatDialog);
  private delegationService = inject(DelegationService);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  @Output() fetchDelegationData = new EventEmitter<number>();
  @Input() delegationBasicInfoFormGroup: UntypedFormGroup;
  @Input() delegationId: number;
  @Input() delegationSettlement: DelegationSettlementDto;
  @Input() isSettlementApproved: boolean;
  @Input() canEdit: boolean;
  @Input() isUserOwnDelegation: boolean;
  @Input() stepper: MatStepper;

  private readonly unsubscribe$ = new Subject<void>();

  private readonly defaultPage: number = 1;
  private readonly defaultPageSize: number = 10;
  private readonly defaultSortColumn: string = 'invoiceNumber';
  private readonly defaultSortDirection: string = 'desc';

  timepickerTimeFormat: number = 24;
  defaultTime: string = '00:00';

  constructor() { }

  private isStartDateChanged = false;
  private isEndDateChanged = false;

  get isDateChanged(): boolean {
    return this.isStartDateChanged || this.isEndDateChanged;
  }

  get startDateControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerStartDate');
  }
  get startTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerStartTime');
  }
  get startDateTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerStartDateTime');
  }
  get initStartDateTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('initPickerStartDateTime');
  }
  get endDateControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerEndDate');
  }
  get endTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerEndTime');
  }
  get endDateTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('pickerEndDateTime');
  }
  get initEndDateTimeControl(): AbstractControl {
    return this.delegationBasicInfoFormGroup.get('initPickerEndDateTime');
  }
  get startDate(): Date {
    return new Date(this.startDateControl.value);
  }
  get startTime(): string {
    return this.startTimeControl.value;
  }
  get startDateTime(): Date {
    return this.startDateTimeControl.value;
  }
  get endDate(): Date {
    return new Date(this.endDateControl.value);
  }
  get endTime(): string {
    return this.endTimeControl.value;
  }
  get endDateTime(): Date {
    return this.endDateTimeControl.value;
  }
  get initStartDateTime(): Date {
    return moment(this.initStartDateTimeControl.value, 'DD-MM-YYYY HH:mm').toDate();
  }
  get initEndDateTime(): Date {
    return moment(this.initEndDateTimeControl.value, 'DD-MM-YYYY HH:mm').toDate();
  }

  ngOnInit(): void {
    const originalValidator = this.delegationBasicInfoFormGroup.validator;
    this.delegationBasicInfoFormGroup.setValidators([originalValidator, this.unsavedDatesValidator.bind(this)].filter((v) => v !== null));
    this.delegationBasicInfoFormGroup.updateValueAndValidity({ emitEvent: false });

    this.onStartDateChanges();
    this.onEndDateChanges();
  }

  startDateFilter = (date: Moment): boolean => !!date && !!this.endDate && date.isSameOrBefore(this.endDate);

  endDateFilter = (date: Moment): boolean => !!date && !!this.startDate && date.isSameOrAfter(this.startDate);

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  isDelegationAccepted(): boolean {
    return this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.Accepted;
  }

  isDelegationCorrection(): boolean {
    return (
      this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.ReturnedFromAccounting ||
      this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.ReturnedFromSupervisor
    );
  }

  isSettlementPendingApproval(): boolean {
    return this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.SettlementPendingApproval;
  }

  isNextButtonVisible(): boolean {
    return !this.isSettlementPendingApproval() && !this.isDelegationAccepted() && !this.isDelegationCorrection();
  }

  isApproveButtonVisible = (): boolean => this.isSettlementPendingApproval() && !this.isUserOwnDelegation;

  isSendCorrectionToAccountingButtonVisible = (): boolean => this.isDelegationCorrection();

  sendToApproval() {
    this.spinner.show();

    this.saveDates().subscribe({
      next: () =>
        this.delegationService.sendCostsToApproval(this.delegationId).subscribe({
          complete: () => this.snackbar.openSuccessSnackBar(Messages.SuccessfullySentToApprovalDelegtion),
        }),
      complete: () => {
        this.spinner.hide();
        this.router.navigate(['/profile', this.delegationSettlement.WorkerId, 'delegations']);
      },
      error: () => this.spinner.hide(),
    });
  }

  public displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  openCostModal() {
    this.dialog
      .open(DelegationAddInvoiceFormComponent, { data: { delegationId: this.delegationId, isEdit: true }, panelClass: 'delegation-invoice-dialog' })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (!isCreated) return;

        this.fetchInvoices();
      });
  }

  addTimeToDate = (date: Date, time: string) => new Date(date.getTime()).setStringTime(time);

  public async goForward() {
    if (this.isDateChanged) {
      const updateSuccess = await this.updateDelegationDates();

      if (!updateSuccess) {
        return;
      }
    }

    if (this.startTime == this.defaultTime && this.endTime == this.defaultTime && this.canEdit) {
      this.openConfirmDialog().then((result) => {
        if (result) {
          this.stepper.next();
        }
      });
    } else {
      this.stepper.next();
    }
  }

  public async saveDatesManually(): Promise<void> {
    if (this.isDateChanged) {
      await this.updateDelegationDates();
    }
  }

  private openDelegationDatesChangedWarning(): void {
    if (this.delegationsInvoicesDataSource.countSubject.value === 0) {
      return;
    }

    this.dialog.open(WarningDialogComponent, {
      data: {
        message: Messages.DelegationDatesChangedWarning,
      },
    });
  }

  private async openConfirmDialog(): Promise<boolean> {
    return await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.ConfirmDelegationHoursTitle,
            message: Messages.ConfirmDelegationHoursMessage,
            confirmButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursApprove),
            dismissButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursCancel),
          },
        })
        .afterClosed(),
    );
  }

  private onStartDateChanges() {
    const dateChanges$ = this.startDateControl.valueChanges.pipe(startWith(this.startDateControl.value));
    const timeChanges$ = this.startTimeControl.valueChanges.pipe(startWith(this.startTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        const dateTime = this.addTimeToDate(this.startDate, this.startTime);
        this.delegationBasicInfoFormGroup.get('pickerStartDateTime').setValue(dateTime, { emitEvent: true });

        const prevStartDate = moment(this.initStartDateTime).format('DD-MM-YYYY HH:mm');
        const nextStartDate = moment(dateTime).format('DD-MM-YYYY HH:mm');

        this.isStartDateChanged = prevStartDate !== nextStartDate;
        this.delegationBasicInfoFormGroup.updateValueAndValidity({ emitEvent: false });
      });
  }

  private onEndDateChanges() {
    const dateChanges$ = this.endDateControl.valueChanges.pipe(startWith(this.endDateControl.value));
    const timeChanges$ = this.endTimeControl.valueChanges.pipe(startWith(this.endTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        const dateTime = this.addTimeToDate(this.endDate, this.endTime);
        this.delegationBasicInfoFormGroup.get('pickerEndDateTime').setValue(dateTime, { emitEvent: false });

        const prevEndDate = moment(this.initEndDateTime).format('DD-MM-YYYY HH:mm');
        const nextEndDate = moment(dateTime).format('DD-MM-YYYY HH:mm');

        this.isEndDateChanged = prevEndDate !== nextEndDate;
        this.delegationBasicInfoFormGroup.updateValueAndValidity({ emitEvent: false });
      });
  }

  private async updateDelegationDates(): Promise<boolean> {
    if (this.delegationBasicInfoFormGroup.invalid && !this.isFormValidExcludingUnsavedDates()) {
      return;
    }

    this.spinner.show();

    try {
      await lastValueFrom(
        this.saveDates().pipe(
          tap({
            next: () => {
              this.snackbar.openSuccessSnackBar(Messages.SuccesfullyUpdatedDelegationDates);
              this.fetchDelegationData.emit(this.delegationId);
              this.resetDateChangedFlag();
              this.openDelegationDatesChangedWarning();
            },
            complete: () => this.spinner.hide(),
          }),
          catchError((error) => {
            this.spinner.hide();
            return this.handleDateUpdateError$(error);
          }),
        ),
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  private handleDateUpdateError$(error: any): Observable<number> {
    const errorCode = error?.error || error;
    if (errorCode === ErrorCode.DelegationSettlementDetailsExists) {
      this.revertDates();

      return this.delegationService.getDelegationSettlementById(null, this.delegationId).pipe(
        take(1),
        tap((dto: DelegationSettlementDto) => {
          const message = this.buildErrorMessage(dto);
          this.dialog.open(ExtendedAlertDialogComponent, {
            data: {
              textArray: message,
            },
          });
        }),
        switchMap(() => throwError(() => error)),
      );
    }

    return throwError(() => error);
  }

  private unsavedDatesValidator(control: AbstractControl): ValidationErrors | null {
    return this.isDateChanged ? { unsavedDates: true } : null;
  }

  private buildErrorMessage(dto: DelegationSettlementDto): string[] {
    let message = [];
    message.push(this.translateService.instant('DelegationSettlementDetailsAlert.HeaderMessage'));

    if (dto.HasMileage) {
      message.push(this.translateService.instant('DelegationSettlementDetailsAlert.Mileage'));
    }
    if (dto.HasSubsistenceAllowanceRate) {
      message.push(this.translateService.instant('DelegationSettlementDetailsAlert.SubsistenceAllowanceRate'));
    }
    if (dto.HasLumpSum) {
      message.push(this.translateService.instant('DelegationSettlementDetailsAlert.LumpSum'));
    }

    message.push(this.translateService.instant('DelegationSettlementDetailsAlert.FooterMessage'));

    return message;
  }

  private revertDates(): void {
    this.startDateControl.setValue(moment(this.initStartDateTime).toDate(), { emitEvent: false });
    this.startTimeControl.setValue(moment(this.initStartDateTime).format('HH:mm'), { emitEvent: false });

    this.endDateControl.setValue(moment(this.initEndDateTime).toDate(), { emitEvent: false });
    this.endTimeControl.setValue(moment(this.initEndDateTime).format('HH:mm'), { emitEvent: false });

    this.startDateTimeControl.setValue(this.initStartDateTime, { emitEvent: false });
    this.endDateTimeControl.setValue(this.initEndDateTime, { emitEvent: false });

    this.resetDateChangedFlag();
  }

  private saveDates(): Observable<number> {
    const request = this.createRequest();

    return this.delegationService.updateDelegationDates(this.delegationId, request);
  }

  private createRequest(): UpdateDelegationDatesRequest {
    return {
      StartDate: this.addTimeToDate(this.startDate, this.startTime),
      EndDate: this.addTimeToDate(this.endDate, this.endTime),
    };
  }

  private fetchInvoices() {
    if (!this.delegationId) {
      return;
    }

    this.delegationsInvoicesDataSource.delegationsInvoicesSubject.next({
      DelegationId: this.delegationId,
      Page: this.defaultPage,
      Count: this.defaultPageSize,
      SortingField: this.defaultSortColumn,
      SortingDirection: this.defaultSortDirection,
    });
  }

  private resetDateChangedFlag() {
    this.isStartDateChanged = this.isEndDateChanged = false;
    this.delegationBasicInfoFormGroup.updateValueAndValidity({ emitEvent: false });
  }

  isFormValidExcludingUnsavedDates(): boolean {
    const errors = this.delegationBasicInfoFormGroup.errors;
    if (!errors) {
      return true;
    }

    const errorKeys = Object.keys(errors);
    return errorKeys.length === 1 && errorKeys[0] === 'unsavedDates';
  }
}
